/**
 * Stable sort for TanStack Table: order is recomputed only on mount and when
 * the user clicks a column sort icon, not when data changes (subscriptions,
 * edits). This avoids the table jumping and users losing their place.
 *
 * Single source of truth for table sorting: use columnSortState + this model only.
 */
import type { Row, RowData, RowModel, Table } from '@tanstack/react-table';
import { getSortedRowModel } from '@tanstack/react-table';

/**
 * Reorders top-level rows to match the given id order. Rows not in idOrder are appended.
 */
function reorderRowsById<TData extends RowData>(
  rows: Row<TData>[],
  idOrder: string[]
): Row<TData>[] {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const idOrderSet = new Set(idOrder);
  const ordered: Row<TData>[] = [];
  for (const id of idOrder) {
    const r = byId.get(id);
    if (r) ordered.push(r);
  }
  for (const r of rows) {
    if (!idOrderSet.has(r.id)) ordered.push(r);
  }
  return ordered;
}

/**
 * Flattens rows (depth-first) to match TanStack Table flatRows structure.
 */
function flattenRows<TData extends RowData>(rows: Row<TData>[]): Row<TData>[] {
  const out: Row<TData>[] = [];
  function visit(row: Row<TData>) {
    out.push(row);
    (row.subRows ?? []).forEach(visit);
  }
  rows.forEach(visit);
  return out;
}

export interface StableSortRefs {
  lastSortingRef: { current: string };
  sortedOrderIdsRef: { current: string[] | null };
}

/**
 * Returns a getSortedRowModel that only recomputes when:
 * - First run (mount), or
 * - User clicks a column sort icon (sorting state changes).
 *
 * Does not recompute when only data changes (subscriptions, inline edits).
 * Uses refs to cache the last sort and row id order; reorders current rows
 * by that order instead of re-sorting. Best practice: one sorting mechanism
 * (columnSortState + this), no duplicate sort state.
 */
export function getStableSortedRowModel<TData extends RowData>(
  refs: StableSortRefs
): (table: Table<TData>) => () => RowModel<TData> {
  const defaultGetSortedRowModel = getSortedRowModel<TData>();

  return (table: Table<TData>) => {
    return (): RowModel<TData> => {
      const preSorted = table.getPreSortedRowModel();
      const sorting = table.getState().sorting;
      const sortingKey = JSON.stringify(sorting);

      const noSort = !sorting?.length || !preSorted.rows.length;
      if (noSort) {
        refs.sortedOrderIdsRef.current = null;
        refs.lastSortingRef.current = '';
        return preSorted;
      }

      const sortChanged =
        refs.sortedOrderIdsRef.current === null ||
        refs.lastSortingRef.current !== sortingKey;

      if (sortChanged) {
        const sortedModel = defaultGetSortedRowModel(table)();
        refs.sortedOrderIdsRef.current = sortedModel.rows.map((r) => r.id);
        refs.lastSortingRef.current = sortingKey;
        return sortedModel;
      }

      const idOrder = refs.sortedOrderIdsRef.current;
      const reorderedRows = reorderRowsById(
        preSorted.rows,
        idOrder ?? preSorted.rows.map((r) => r.id)
      );
      const flatRows = flattenRows(reorderedRows);

      return {
        rows: reorderedRows,
        flatRows,
        rowsById: preSorted.rowsById,
      };
    };
  };
}
